/**
 * Utility helpers for the Procurement Inquiry Workflow.
 */

import { buildSupplierSummary } from './wizardHelpers';

/**
 * Build a unique Inquiry ID in the format INQ-{clientId}-{YYYYMMDD}.
 * Appends a zero-padded counter suffix (-02, -03, …) when multiple
 * inquiries are created for the same client on the same day.
 *
 * @param {string} clientId - e.g. 'L001', 'C002'
 * @param {string} creationDate - ISO date string 'YYYY-MM-DD'
 * @param {Array}  existingInquiries - current inquiries array
 * @returns {string}
 */
export const buildInquiryId = (clientId, creationDate, existingInquiries = []) => {
  const compact = creationDate.replace(/-/g, '');
  const base = `INQ-${clientId}-${compact}`;
  const collisions = existingInquiries.filter(
    (i) => i.id === base || i.id.startsWith(`${base}-`),
  ).length;
  return collisions === 0 ? base : `${base}-${String(collisions + 1).padStart(2, '0')}`;
};

/**
 * Build a unique Quote ID in the format QUO-{clientId}-{YYYYMMDD}, following
 * the same collision-suffix scheme as buildInquiryId.
 *
 * @param {string} clientId
 * @param {string} creationDate - ISO date string 'YYYY-MM-DD'
 * @param {Array}  existingQuotes - current quotes array
 * @returns {string}
 */
export const buildQuoteId = (clientId, creationDate, existingQuotes = []) => {
  const compact = creationDate.replace(/-/g, '');
  const base = `QUO-${clientId}-${compact}`;
  const collisions = existingQuotes.filter(
    (q) => q.id === base || q.id.startsWith(`${base}-`),
  ).length;
  return collisions === 0 ? base : `${base}-${String(collisions + 1).padStart(2, '0')}`;
};

/**
 * Calculate total internal cost for a single quotation item.
 * All values default to 0 when undefined/null.
 *
 * @param {number} baseInternalPrice
 * @param {number} productionCost
 * @param {number} deliveryCost
 * @returns {number}
 */
export const computeTotalInternalCost = (baseInternalPrice = 0, productionCost = 0, deliveryCost = 0) =>
  Number(baseInternalPrice || 0) + Number(productionCost || 0) + Number(deliveryCost || 0);

/**
 * Map inquiry items (with completed quotations) to the offerItems shape
 * expected by OfferOrderModal / handleSaveOfferOrder.
 *
 * @param {object} inquiry - a full Inquiry record
 * @returns {Array} offerItems
 */
export const buildOfferItemsFromInquiry = (inquiry) =>
  (inquiry.items || []).map((item, index) => {
    const q = item.quotation || {};
    // Quote Products can have an independent Branding-role supplier (see
    // QuoteDetailPage), but the Offer/Order line-item model only has three
    // cost buckets — fold the branding cost into "production cost" here so
    // the total dollar amount survives the Quote -> Offer boundary exactly,
    // even though the 4-way breakdown collapses to 3 buckets at that point.
    const productionAndBranding = Number(q.productionCost || 0) + Number(q.brandingCost || 0);
    const totalInternal = computeTotalInternalCost(
      q.baseInternalPrice,
      productionAndBranding,
      q.deliveryCost,
    );
    const customerUnit = computeTotalCustomer(
      computeCustomerPrice(
        computeTotalInternalCost(q.baseCustomerPrice, productionAndBranding, q.deliveryCost),
        0,
      ),
      1,
      q.discount || 0,
    );
    return {
      id: `${inquiry.id}-item-${index + 1}`,
      lineType: item.lineType || 'Product',
      serviceId: item.serviceId ?? null,
      logisticsId: item.logisticsId ?? null,
      linkedToLineId: item.linkedToLineId ?? null,
      productId: item.productId || null,
      productName: item.productName || '',
      article: '',
      supplierName: q.supplierName || '',
      quantity: item.quantity || 0,
      qty: item.quantity || 0,
      baseInternalPrice: Number(q.baseInternalPrice || 0),
      productionCost: productionAndBranding,
      deliveryCost: Number(q.deliveryCost || 0),
      baseCustomerPrice: Number(q.baseCustomerPrice || 0),
      internalPrice: totalInternal,
      customerPrice: customerUnit,
      margin: computeMarginPercent(totalInternal, customerUnit),
      discount: q.discount || 0,
      internalLineTotal: totalInternal * (item.quantity || 0),
      lineTotal: customerUnit * (item.quantity || 0),
      notes: [item.brandingDescription, item.deliveryRequirements]
        .filter(Boolean)
        .join(' | '),
      brandingDescription: item.brandingDescription || '',
      deliveryRequirements: item.deliveryRequirements || '',
      categoryName: item.categoryName || '',
      preliminaryDeliveryTime: q.preliminaryDeliveryTime || q.leadTime || '',
      files: item.files || [],
      // source reference
      inquiryItemId: item.id,
    };
  });

/**
 * Returns true when the user has a Sales role.
 * @param {object|null} user
 * @returns {boolean}
 */
export const isSalesUser = (user) => {
  if (!user) return false;
  return ['sales user', 'sales manager', 'admin'].includes(user.role);
};

/**
 * Returns true only for the dedicated Sales roles (not admin/procurement) —
 * used to hide supplier names from Sales-only users on the Quote Detail page.
 * @param {object|null} user
 * @returns {boolean}
 */
export const isSalesOnlyUser = (user) => {
  if (!user) return false;
  return ['sales user', 'sales manager'].includes(user.role);
};

/**
 * Returns true when the user has a Procurement role.
 * @param {object|null} user
 * @returns {boolean}
 */
export const isProcurementUser = (user) => {
  if (!user) return false;
  return ['procurement user', 'procurement manager', 'admin'].includes(user.role);
};

/**
 * Compute customer price from internal cost and margin percentage.
 * @param {number} internalCost
 * @param {number} marginPct - e.g. 30 for 30%
 * @returns {number}
 */
export const computeCustomerPrice = (internalCost, marginPct) =>
  Number(internalCost || 0) * (1 + Number(marginPct || 0) / 100);

/**
 * Compute margin percentage from internal cost and customer price.
 * @param {number} internalCost
 * @param {number} customerPrice
 * @returns {number}
 */
export function computeMarginPercent(internalCost, customerPrice) {
  const cost = Number(internalCost || 0);
  if (cost === 0) return 0;
  return ((Number(customerPrice || 0) / cost) - 1) * 100;
}

/**
 * Compute total customer line value.
 * @param {number} customerPrice
 * @param {number} quantity
 * @param {number} discountPct - e.g. 10 for 10%
 * @returns {number}
 */
export const computeTotalCustomer = (customerPrice, quantity, discountPct) =>
  Number(customerPrice || 0) * Number(quantity || 0) * (1 - Number(discountPct || 0) / 100);

// ─────────────────────────────────────────────────────────────────────────
// Inquiry_Item CRUD (multi-product Inquiry — procurement-workflow-redesign)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Validates the required fields of an Inquiry_Item submission
 * (inquiry-module-redesign): a line item is sourced by Category rather than
 * a specific product — the product/service/logistics choices are made later
 * in the SelectionWizard — but otherwise keeps the same fields/requiredness
 * the form has always had.
 * @returns {{categoryId?: string, quantity?: string, specifications?: string}} error map (empty when valid)
 */
export const validateInquiryItem = (data = {}) => {
  const errors = {};
  if (data.categoryId == null) errors.categoryId = 'Category is required';
  if (!Number.isInteger(Number(data.quantity)) || Number(data.quantity) < 1) errors.quantity = 'Quantity must be at least 1';
  if (!String(data.specifications || '').trim()) errors.specifications = 'Specifications are required';
  return errors;
};

/**
 * Builds a new Inquiry_Item from form data, initialised with
 * sourcingStatus 'Pending' and no wizard selections yet. The specific
 * products/services/logistics choices are captured later by the
 * SelectionWizard and stored in `wizardResult`; everything else (description,
 * target price, branding, special requirements, attachments) is the same
 * shape the form has always captured.
 */
export const buildInquiryItem = (data = {}) => ({
  id: data.id || `ITEM-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
  categoryId: data.categoryId ?? null,
  categoryName: data.categoryName || '',
  quantity: Number(data.quantity) || 1,
  specifications: data.specifications || '',
  targetPrice: data.targetPrice ?? null,
  brandingDescription: data.brandingDescription || '',
  deliveryRequirements: data.deliveryRequirements || '',
  reorder: Boolean(data.reorder),
  sourcingStatus: data.sourcingStatus || 'Pending',
  wizardResult: data.wizardResult ?? null,
  files: data.files || [],
});

export const updateInquiryItem = (items = [], id, patch = {}) => (
  items.map((item) => (item.id === id ? { ...item, ...patch } : item))
);

export const deleteInquiryItem = (items = [], id) => items.filter((item) => item.id !== id);

/**
 * "Create Quotes" is enabled only once every Inquiry_Item has been sourced
 * through the SelectionWizard (a saved `wizardResult`).
 */
export const canCreateQuotes = (items = []) => (
  items.some((item) => item.wizardResult != null)
);

/**
 * Deterministic Quote id for a given inquiry item: QUO-{clientId}-{YYYYMMDD}-{n}
 */
export const buildQuoteIdForItem = (clientId, creationDate, itemIndex) => {
  const compact = String(creationDate).replace(/-/g, '');
  return `QUO-${clientId}-${compact}-${itemIndex + 1}`;
};

/**
 * Produces one Quote per Inquiry_Item that has been sourced through the
 * SelectionWizard (a saved `wizardResult`), skipping items that already
 * have a non-archived Quote (matched via inquiryItemId) so repeated calls
 * are idempotent. The Quote's `suppliers` list is the wizard's per-supplier
 * role summary (Manufacturing/Services); its `logisticsNotes` carries the
 * wizard's free-text Logistics note along (no supplier of its own); and its
 * display name falls back through the selected products' names to the
 * item's category.
 */
export const buildQuotesFromInquiry = (inquiry, existingQuotes = [], itemIds = null) => {
  const creationDate = new Date().toISOString().slice(0, 10);
  const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
  return (inquiry.items || [])
    .filter((item) => item.wizardResult != null)
    .filter((item) => !itemIds || itemIds.includes(item.id))
    .filter((item) => item.sourcingStatus !== 'Quote Created')
    .filter((item) => !existingQuotes.some((quote) => quote.inquiryItemId === item.id && !quote.archived))
    .map((item) => {
      const itemIndex = (inquiry.items || []).findIndex((inquiryItem) => inquiryItem.id === item.id);
      const quoteId = buildQuoteIdForItem(inquiry.clientId, creationDate, itemIndex);
      const { selectedProducts = [], selectedServices = [], logisticsNotes = '' } = item.wizardResult || {};
      const productNames = Array.from(new Set(selectedProducts.map((ref) => ref.productName).filter(Boolean)));
      const displayName = productNames.length > 0 ? productNames.join(', ') : (item.categoryName || '');
      return {
      id: quoteId,
      key: quoteId,
      type: 'Quote',
      lineType: 'Product',
      serviceId: null,
      logisticsId: null,
      linkedToLineId: null,
      inquiryId: inquiry.id,
      inquiryItemId: item.id,
      clientId: inquiry.clientId,
      client: inquiry.client,
      country: inquiry.country || 'Denmark',
      createdBy: inquiry.createdBy || inquiry.requestedBy || 'System',
      requestedBy: inquiry.requestedBy,
      deliveryAddress: inquiry.deliveryAddress || '',
      neededBefore: inquiry.neededBefore,
      requestedDeliveryDate: inquiry.requestedDeliveryDate,
      productId: selectedProducts[0]?.productId ?? null,
      productName: displayName,
      categoryName: item.categoryName || '',
      specifications: item.specifications || '',
      productCategory: item.categoryName || '',
      quantity: item.quantity,
      targetPrice: item.targetPrice ?? null,
      suppliers: buildSupplierSummary(selectedProducts, selectedServices, []),
      // Logistics has no supplier of its own on the wizard (a free-text
      // note, not a catalogue selection) — carried onto the Quote as-is.
      logisticsNotes,
      supplierQuotes: {},
      status: 'Ready for Procurement',
      priority: inquiry.priority,
      reorder: Boolean(item.reorder),
      nextStep: 'Awaiting supplier pricing',
      archived: false,
      convertedOfferId: null,
      createdAt: now,
      brandingDescription: item.brandingDescription || '',
      deliveryRequirements: item.deliveryRequirements || '',
      files: item.files || [],
      };
    });
};
